import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Download,
  RotateCcw,
  Shield,
  AlertTriangle,
  FileJson,
  UserPlus,
  Settings,
  Clock,
  Activity,
  ChevronRight,
  Database,
  Loader2,
} from 'lucide-react';
import { fetchAuditLogs } from '../services/auditLogService';
import { Select } from '../components';

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  { value: 'USER_ACCESS', label: 'User Access' },
  { value: 'RESIDENT', label: 'Resident' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'SYSTEM', label: 'System' },
];

const RESULT_OPTIONS = [
  { value: 'all', label: 'All Results' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'FAILED', label: 'Failed' },
];

const RANGE_OPTIONS = [
  { value: '30d', label: 'Last 30 days' },
  { value: '7d', label: 'Last 7 days' },
  { value: 'today', label: 'Today' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })} GMT`;
};

const categoryLabel = (c) => (c || '').replace(/_/g, ' ');

// MySQL columns typed as TEXT (not native JSON) come back as a string. Parse
// it transparently so the details panel renders proper key/value rows
// instead of iterating the JSON string character-by-character.
const normalizeDetails = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return { value: raw }; }
  }
  return null;
};

const AuditLogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedResult, setSelectedResult] = useState('all');
  const [selectedRange, setSelectedRange] = useState('30d');
  const [expandedRow, setExpandedRow] = useState(null);

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ all: 0, security: 0, failed: 0, user_access: 0, system: 0, resident: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        search: searchTerm || undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        result: selectedResult !== 'all' ? selectedResult : undefined,
        range: selectedRange,
        limit: 100,
      };
      const res = await fetchAuditLogs(params);
      setLogs(res.data || []);
      setStats(res.stats || {});
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedResult, selectedRange]);

  // Debounce search separately so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => { loadLogs(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleReset = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedResult('all');
    setSelectedRange('30d');
  };

  const statCards = useMemo(() => ([
    { title: 'ALL EVENTS', count: stats.all || 0, subtitle: 'Full audit view', color: 'blue', icon: Activity },
    { title: 'SECURITY', count: stats.security || 0, subtitle: 'Login, password, risk', color: 'red', icon: Shield },
    { title: 'FAILED', count: stats.failed || 0, subtitle: 'Needs review', color: 'orange', icon: AlertTriangle },
    { title: 'USER ACCESS', count: stats.user_access || 0, subtitle: 'Manager and staff', color: 'amber', icon: UserPlus },
    { title: 'RESIDENT', count: stats.resident || 0, subtitle: 'Enquiries and clients', color: 'purple', icon: Database },
    { title: 'SYSTEM', count: stats.system || 0, subtitle: 'Email and API events', color: 'emerald', icon: Settings },
  ]), [stats]);

  const getResultColor = (result) => {
    switch (result) {
      case 'SUCCESS': return 'bg-green-100 text-green-700 border-green-200 dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-900/50';
      case 'WARNING': return 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/10 dark:text-orange-400 dark:border-orange-900/50';
      case 'FAILED': return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/10 dark:text-red-400 dark:border-red-900/50';
      default: return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'SECURITY': return 'bg-red-50 text-red-600 border-red-100 dark:bg-red-900/10 dark:text-red-400 dark:border-red-900/50';
      case 'USER_ACCESS': return 'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-900/50';
      case 'RESIDENT': return 'bg-purple-50 text-purple-600 border-purple-100 dark:bg-purple-900/10 dark:text-purple-400 dark:border-purple-900/50';
      case 'SYSTEM': return 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-900/50';
      default: return 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/50';
    }
  };

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500 font-sans">

      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-[26px] font-bold text-[#1E293B] dark:text-gray-200 tracking-tight">Audit Logs</h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1 max-w-2xl leading-relaxed">
            Live activity feed across the platform. Tracks manager and staff creation, new
            enquiries, and other system events. Click a row for full event details.
          </p>
        </div>
        <div className="flex gap-2.5">
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 text-[#475569] dark:text-gray-400 text-[13px] font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-[#31373f] transition-all shadow-sm">
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white text-[13px] font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-sm">
            <Download className="w-4 h-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#262b33] p-4 rounded-xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <input
            type="text"
            placeholder="Search action, actor, organisation, target..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#F8FAFC] dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 focus:border-[#2563EB] text-[14px] text-[#1E293B] dark:text-gray-200 transition-all"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select
            value={selectedCategory}
            onChange={(v) => setSelectedCategory(v)}
            options={CATEGORY_OPTIONS}
            className="w-[170px]"
          />
          <Select
            value={selectedResult}
            onChange={(v) => setSelectedResult(v)}
            options={RESULT_OPTIONS}
            className="w-[150px]"
          />
          <Select
            value={selectedRange}
            onChange={(v) => setSelectedRange(v)}
            options={RANGE_OPTIONS}
            className="w-[150px]"
          />
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white text-[13px] font-bold rounded-lg hover:bg-blue-700 transition-all shadow-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card, idx) => (
          <div
            key={idx}
            className={`bg-white dark:bg-[#262b33] p-4 rounded-xl border-l-4 shadow-sm border border-[#E2E8F0] dark:border-gray-800 hover:shadow-md transition-all group ${
              idx === 0 ? 'border-l-blue-500 ring-2 ring-blue-500/10 bg-blue-50/20' :
              card.color === 'red' ? 'border-l-red-500' :
              card.color === 'orange' ? 'border-l-orange-500' :
              card.color === 'purple' ? 'border-l-purple-500' :
              card.color === 'amber' ? 'border-l-amber-500' :
              'border-l-emerald-500'
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className={`text-[11px] font-bold uppercase tracking-wider ${idx === 0 ? 'text-blue-600' : 'text-[#64748B] dark:text-gray-400'}`}>
                {card.title}
              </span>
              <card.icon className={`w-4 h-4 ${idx === 0 ? 'text-blue-500' : 'text-[#94A3B8] dark:text-gray-500 group-hover:text-gray-600'}`} />
            </div>
            <div className="text-[24px] font-bold text-[#1E293B] dark:text-gray-200">{card.count}</div>
            <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{card.subtitle}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#262b33] rounded-xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#F8FAFC] dark:bg-[#31373f] border-b border-[#E2E8F0] dark:border-gray-800">
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Action</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Organisation / Target</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Result</th>
                <th className="px-6 py-4 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9] dark:divide-gray-800">
              {loading && (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center text-[#64748B] dark:text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
                    Loading audit events...
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-red-600 font-semibold">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && logs.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center text-[#64748B] dark:text-gray-400">
                    No audit events match the current filters.
                  </td>
                </tr>
              )}
              {!loading && !error && logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className={`hover:bg-[#F8FAFC] dark:hover:bg-[#31373f] transition-colors cursor-pointer ${expandedRow === log.id ? 'bg-[#F8FAFC] dark:bg-[#31373f]' : ''}`}
                    onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                  >
                    <td className="px-6 py-5 align-top">
                      <div className="text-[14px] font-bold text-blue-600">{formatDate(log.created_at)}</div>
                      <div className="text-[12px] text-[#64748B] dark:text-gray-400 font-medium mt-1 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        {formatTime(log.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg shrink-0 ${
                          log.category === 'SECURITY' ? 'bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400' :
                          log.category === 'USER_ACCESS' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/10 dark:text-amber-400' :
                          log.category === 'RESIDENT' ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/10 dark:text-purple-400' :
                          'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                        }`}>
                          {log.category === 'SECURITY' ? <Shield className="w-4 h-4" /> :
                            log.category === 'USER_ACCESS' ? <UserPlus className="w-4 h-4" /> :
                            log.category === 'RESIDENT' ? <Database className="w-4 h-4" /> :
                            <Activity className="w-4 h-4" />}
                        </div>
                        <div>
                          <div className="text-[14px] font-bold text-[#1E293B] dark:text-gray-200 leading-tight">{log.action}</div>
                          <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 font-medium">
                            {log.actor_name || 'System'}{log.actor_role ? ` · ${log.actor_role}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="text-[14px] font-bold text-[#475569] dark:text-gray-400">{log.organization_name || '—'}</div>
                      <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1">{log.target || ''}</div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wider ${getCategoryColor(log.category)}`}>
                        {categoryLabel(log.category)}
                      </span>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wider ${getResultColor(log.result)}`}>
                        {log.result}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right align-top">
                      <button className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-[#31373f] transition-all ${expandedRow === log.id ? 'rotate-90 bg-gray-100 dark:bg-[#31373f]' : ''}`}>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8] dark:text-gray-500" />
                      </button>
                    </td>
                  </tr>

                  {expandedRow === log.id && (
                    <tr>
                      <td colSpan="6" className="px-8 py-6 bg-[#F8FAFC] dark:bg-[#20242c] border-t border-b border-[#E2E8F0] dark:border-gray-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-4">
                            <h4 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200 flex items-center gap-2">
                              <FileJson className="w-4 h-4 text-blue-500" />
                              Technical Details
                            </h4>
                            <div className="bg-white dark:bg-[#262b33] p-4 rounded-lg border border-[#E2E8F0] dark:border-gray-800 font-mono text-[12px] text-[#475569] dark:text-gray-400 overflow-x-auto shadow-sm">
                              {(() => {
                                const d = normalizeDetails(log.details);
                                if (!d || Object.keys(d).length === 0) {
                                  return <div className="text-[#94A3B8] dark:text-gray-500">No additional details</div>;
                                }
                                return Object.entries(d).map(([key, value]) => (
                                  <div key={key} className="flex gap-4 py-1 border-b border-gray-50 last:border-0">
                                    <span className="font-bold text-[#64748B] dark:text-gray-400 w-32 shrink-0">{key}:</span>
                                    <span className="break-all">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                  </div>
                                ));
                              })()}
                              {log.ip_address && (
                                <div className="flex gap-4 py-1 border-t border-gray-50 mt-1">
                                  <span className="font-bold text-[#64748B] dark:text-gray-400 w-32 shrink-0">ip_address:</span>
                                  <span>{log.ip_address}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-4">
                            <h4 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200 flex items-center gap-2">
                              <Database className="w-4 h-4 text-blue-500" />
                              Trace Summary
                            </h4>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 text-[13px]">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-[#64748B] dark:text-gray-400">Action identified as <span className="text-[#1E293B] dark:text-gray-200 font-semibold">{log.action}</span></span>
                              </div>
                              <div className="flex items-center gap-3 text-[13px]">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                <span className="text-[#64748B] dark:text-gray-400">Performed by <span className="text-[#1E293B] dark:text-gray-200 font-semibold">{log.actor_name || 'System'}</span></span>
                              </div>
                              <div className="flex items-center gap-3 text-[13px]">
                                <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                                <span className="text-[#64748B] dark:text-gray-400">Timestamp <span className="text-[#1E293B] dark:text-gray-200 font-semibold">{formatDate(log.created_at)} {formatTime(log.created_at)}</span></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
